import React, { useCallback, useEffect, useState } from 'react';
import { resetPassword } from '../../auth/services/AuthService';
import { useLocation } from 'react-router-dom';
import { useTranslations } from '../../localization/useTranslations';
import Button from '../../components/buttons/SimpleButton/Button';
import Icon from '../../common/components/Icon';
import AuthenticationHeader from './AuthenticationHeader';

interface PasswordResetProps {
  showSnackbar: (message: string) => void;
}

const PasswordReset: React.FC<PasswordResetProps> = ({ showSnackbar }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [focused, setFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const translations = useTranslations();
  const authSlug = window.authenticationPageSlug || 'authentication';

  const [validationResults, setValidationResults] = useState({
    lowerCase: false,
    upperCase: false,
    numberOrSpecialChar: false,
    length: false,
  });

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get('token');

  const handleFocus = () => {
    setFocused(true);
  };

  const handleBlur = () => {
    setFocused(false);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const checkForPasswordErrors = useCallback(() => {
    const lowerCaseRegex = /[a-z]/;
    const upperCaseRegex = /[A-Z]/;
    // eslint-disable-next-line
    const numberOrSpecialCharRegex =
      // eslint-disable-next-line
      /[0-9!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/;
    // eslint-disable-next-line
    const lengthRegex = /.{8,}/;

    const lowerCase = lowerCaseRegex.test(password);
    const upperCase = upperCaseRegex.test(password);
    const numberOrSpecialChar = numberOrSpecialCharRegex.test(password);
    const length = lengthRegex.test(password);

    setValidationResults({
      lowerCase,
      upperCase,
      numberOrSpecialChar,
      length,
    });

    if (lowerCase && upperCase && numberOrSpecialChar && length) {
      setIsLoading(false);
    } else {
      setIsLoading(true);
    }
  }, [password]);

  const handleResetPassword = async () => {
    if (!password) return;
    setError('');
    setIsLoading(true);

    try {
      if (!token) {
        showSnackbar(translations?.snackBarMessages?.tokenMissing || 'Token is missing.');

        return;
      }

      const response = await resetPassword(password, token);

      if (response.status !== 'ok') {
        showSnackbar(translations?.snackBarMessages?.tokenInvalid || 'Token is invalid or expired.');
      } else {
        showSnackbar(
          translations?.snackBarMessages?.passwordChanged || 'Password successfully changed, please log in.'
        );

        setTimeout(() => {
          window.location.href = window.localStorage.getItem('resetPasswordLocation') || `/${authSlug}`;

          window.localStorage.removeItem('resetPasswordLocation');
        }, 3000);
      }
    } catch (error) {
      showSnackbar(translations?.snackBarMessages?.somethingWentWrong || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkForPasswordErrors();
  }, [checkForPasswordErrors]);

  return (
    <>
      <AuthenticationHeader isPasswordResetPage />
      <div className="login-modal__form login-modal__form--exist">
        <div className="input-group">
          <label className={`input-group__label ${focused ? 'focused' : ''}`}>
            {translations?.authLabels?.passLabel || 'Password'}
          </label>
          <div className="password-input">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={translations?.authLabels?.passwordPlaceholder || 'Password'}
              className={error ? 'input-group__input input-group__input--error error' : 'input-group__input'}
              autoFocus
              onFocus={handleFocus}
              onBlur={handleBlur}
            />
            <span className="password-toggle" onClick={togglePasswordVisibility}>
              {showPassword ? <Icon name="eye-off" size="medium" /> : <Icon name="eye-on" size="medium" />}
            </span>
          </div>

          {error && <p className="error">{error}</p>}
        </div>

        <div className="login-modal__live-validation">
          <p className="basic-14">{translations?.authLabels?.passStrong + ':' || 'Password strength:'}</p>
          <ul>
            <li id="error-requiredLowerCase" className={`basic-14 ${validationResults.lowerCase ? 'passed' : ''}`}>
              {translations?.authLabels?.oneLowerLetter || 'one lower letter'}
            </li>
            <li id="error-requiredUpperCase" className={`basic-14 ${validationResults.upperCase ? 'passed' : ''}`}>
              {translations?.authLabels?.oneCapitalLetter || 'one capital letter'}
            </li>
            <li
              id="error-requiredNumberOrSpecialCharacter"
              className={`basic-14 ${validationResults.numberOrSpecialChar ? 'passed' : ''}`}
            >
              {translations?.authLabels?.oneNumberOrSpecialChar || 'one number or special character'}
            </li>
            <li id="error-requiredLength" className={`basic-14 ${validationResults.length ? 'passed' : ''}`}>
              {translations?.authLabels?.minCharacters || 'min. 8 characters'}
            </li>
          </ul>
        </div>

        <Button
          className=""
          label={translations?.authLabels?.setANewPassword || 'Set new password'}
          type="primary"
          size="large"
          onClick={handleResetPassword}
          isDisabled={isLoading}
          fullWidth={true}
        />
      </div>
    </>
  );
};

export default PasswordReset;
